import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const HOME_COLLECTIONS_CACHE_KEY = 'ourooh:mobile-menu-collections:v1';
const HOME_COLLECTIONS_CACHE_TTL = 5 * 60 * 1000;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #homeCollectionsLoaded = false;

  /** @type {Promise<void> | null} */
  #homeCollectionsPromise = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('shopify:section:load', this.#onShopifySectionLoad);
    document.addEventListener('DOMContentLoaded', this.#cacheLocalHomeCollections, { once: true });
    if (document.readyState !== 'loading') this.#cacheLocalHomeCollections();
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('shopify:section:load', this.#onShopifySectionLoad);
    document.removeEventListener('DOMContentLoaded', this.#cacheLocalHomeCollections);
  }

  /** Cache homepage markup without making a request when it is already in the DOM. */
  #cacheLocalHomeCollections = () => {
    const source = document.querySelector('template[data-mobile-menu-collections-source]');
    if (source instanceof HTMLTemplateElement) this.#cacheHomeCollectionsSource(source);
  };

  /**
   * Keep the drawer preview in sync while the homepage collection section is
   * changed in the theme editor.
   * @param {Event} event
   */
  #onShopifySectionLoad = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.querySelector('template[data-mobile-menu-collections-source]');
    if (!(source instanceof HTMLTemplateElement)) return;

    this.#cacheHomeCollectionsSource(source);
    this.#homeCollectionsLoaded = false;
    this.#homeCollectionsPromise = null;

    if (this.isOpen && this.#isMobileViewport()) {
      this.#renderHomeCollections(source);
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      void this.#loadHomeCollections();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Load the first three collections from the homepage collection-list section.
   * This only runs on mobile and only after the drawer is opened.
   * @returns {Promise<void>}
   */
  async #loadHomeCollections() {
    if (!this.#isMobileViewport() || this.#homeCollectionsLoaded) return;
    if (this.#homeCollectionsPromise) return this.#homeCollectionsPromise;

    const container = this.querySelector('[data-home-collections]');
    if (!(container instanceof HTMLElement)) return;

    container.hidden = false;
    container.setAttribute('aria-busy', 'true');

    this.#homeCollectionsPromise = (async () => {
      const source = await this.#resolveHomeCollectionsSource();
      if (!source || !this.#renderHomeCollections(source)) {
        container.hidden = true;
      }
    })()
      .catch(() => {
        container.hidden = true;
      })
      .finally(() => {
        this.#homeCollectionsPromise = null;
      });

    return this.#homeCollectionsPromise;
  }

  /**
   * Prefer the homepage source already in the DOM, then a short-lived session
   * cache. A direct landing on another page requests the homepage only when the
   * drawer opens; the detached images do not load until the three cards render.
   * @returns {Promise<HTMLTemplateElement | null>}
   */
  async #resolveHomeCollectionsSource() {
    const localSource = document.querySelector('template[data-mobile-menu-collections-source]');
    if (localSource instanceof HTMLTemplateElement) {
      this.#cacheHomeCollectionsSource(localSource);
      return localSource;
    }

    const cachedSource = this.#getCachedHomeCollectionsSource();
    if (cachedSource) return cachedSource;

    const rootUrl = new URL(this.dataset.homeCollectionsRootUrl || '/', window.location.origin);
    const fetchedSource = await this.#fetchHomeCollectionsSource(rootUrl);
    if (fetchedSource) this.#cacheHomeCollectionsSource(fetchedSource);

    return fetchedSource;
  }

  /**
   * @param {URL} url
   * @returns {Promise<HTMLTemplateElement | null>}
   */
  async #fetchHomeCollectionsSource(url) {
    const response = await fetch(url, {
      credentials: 'same-origin',
    });

    if (!response.ok) return null;

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = documentFragment.querySelector('template[data-mobile-menu-collections-source]');
    return source instanceof HTMLTemplateElement ? source : null;
  }

  /**
   * @param {HTMLTemplateElement} source
   */
  #cacheHomeCollectionsSource(source) {
    try {
      window.sessionStorage.setItem(
        HOME_COLLECTIONS_CACHE_KEY,
        JSON.stringify({ markup: source.innerHTML, cachedAt: Date.now() })
      );
    } catch {
      // Storage may be unavailable in privacy modes; the drawer still works.
    }
  }

  /** @returns {HTMLTemplateElement | null} */
  #getCachedHomeCollectionsSource() {
    try {
      const cachedValue = window.sessionStorage.getItem(HOME_COLLECTIONS_CACHE_KEY);
      if (!cachedValue) return null;

      const cached = JSON.parse(cachedValue);
      if (
        typeof cached?.markup !== 'string' ||
        typeof cached?.cachedAt !== 'number' ||
        Date.now() - cached.cachedAt > HOME_COLLECTIONS_CACHE_TTL
      ) {
        window.sessionStorage.removeItem(HOME_COLLECTIONS_CACHE_KEY);
        return null;
      }

      const source = document.createElement('template');
      source.innerHTML = cached.markup;
      return source;
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLTemplateElement} source
   * @returns {boolean}
   */
  #renderHomeCollections(source) {
    const container = this.querySelector('[data-home-collections]');
    const list = this.querySelector('[data-home-collections-list]');
    if (!(container instanceof HTMLElement) || !(list instanceof HTMLUListElement)) return false;

    const content = source.content.cloneNode(true);
    if (!(content instanceof DocumentFragment) || content.childElementCount === 0) return false;

    list.replaceChildren(content);
    container.hidden = false;
    container.setAttribute('aria-busy', 'false');
    container.classList.add('is-loaded');
    this.#homeCollectionsLoaded = true;
    return true;
  }

  /** @returns {boolean} */
  #isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
